import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const MIN_WIDTH = 354;

/** Selectors for all page containers that must shrink when the chat panel resizes */
const PAGE_CONTAINER_SELECTORS = [
    '.landing-page',
    '.policy-snapshot-container',
    '.profile-activities-container',
    '.call-intent-container',
];

@Injectable({ providedIn: 'root' })
export class ChatbotStateService {
    private _width = new BehaviorSubject<number>(MIN_WIDTH);
    readonly width$ = this._width.asObservable();

    setWidth(value: number): void {
        this._width.next(value);
        this._applyToDOM(value);
    }

    get width(): number {
        return this._width.getValue();
    }

    /**
     * Called after route navigation so newly-rendered page containers
     * immediately pick up the current chatbot width without waiting for a drag.
     */
    applyCurrentWidthToDOM(): void {
        this._applyToDOM(this._width.getValue());
    }

    /**
     * Called when the chatbot is closed — removes the inline right offset from
     * all page containers so they expand back to full width, and resets the
     * stored width to the minimum so reopening starts fresh.
     */
    clearPageContainerOffsets(): void {
        this._preserveAndRestoreScroll(() => {
            this._width.next(MIN_WIDTH);
            document.documentElement.style.removeProperty('--chatbot-width');
            for (const selector of PAGE_CONTAINER_SELECTORS) {
                const el = document.querySelector(selector) as HTMLElement | null;
                if (el) el.style.right = '';
            }
        });
    }

    // Legacy boolean helper used by app.component for the landing page right offset
    setExpanded(value: boolean): void {
        this.setWidth(value ? 680 : MIN_WIDTH);
    }

    private _applyToDOM(width: number): void {
        this._preserveAndRestoreScroll(() => {
            // CSS custom property on :root — available to all stylesheets
            document.documentElement.style.setProperty('--chatbot-width', width + 'px');

            // Write right offset directly to any currently-rendered page container
            for (const selector of PAGE_CONTAINER_SELECTORS) {
                const el = document.querySelector(selector) as HTMLElement | null;
                if (el) el.style.right = width + 'px';
            }
        });
    }

    /**
     * Preserves scroll position of page containers across a DOM change
     * that could cause reflow and scroll reset.
     */
    private _preserveAndRestoreScroll(action: () => void): void {
        // Capture current scroll positions
        const scrollPositions: { el: HTMLElement; top: number }[] = [];
        for (const selector of PAGE_CONTAINER_SELECTORS) {
            const el = document.querySelector(selector) as HTMLElement | null;
            if (el) {
                scrollPositions.push({ el, top: el.scrollTop });
            }
        }

        // Perform the DOM change
        action();

        // Restore scroll positions after reflow
        requestAnimationFrame(() => {
            for (const { el, top } of scrollPositions) {
                el.scrollTop = top;
            }
        });
    }
}
